import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    isDevMode,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import {
    ApiTypes,
    ContactFeature,
    DealerFeature,
    RouterLinkDirective,
    getErrorMessage,
    numericValidator,
} from '@hyundai/ng-common-lib';
import { GENDER } from '@hyundai/shared-lib';
import {
    InputRadioComponent,
    InputSelectComponent,
    InputTextComponent,
    InputTextareaComponent,
    M000C010ImageComponent,
    M000C040LoadingSpinnerComponent,
} from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { WidgetInstance } from 'friendly-challenge';
@Component({
    selector: 'app-contact-page',
    standalone: true,
    imports: [
        RouterLinkDirective,
        InputTextareaComponent,
        InputTextComponent,
        InputSelectComponent,
        InputRadioComponent,
        M000C010ImageComponent,
        M000C040LoadingSpinnerComponent,
        ReactiveFormsModule,
    ],
    templateUrl: './contact-page.component.html',
    styleUrl: './contact-page.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactPageComponent {
    store = inject(Store);
    router = inject(Router);
    friendlyCaptcha = viewChild<ElementRef<HTMLElement>>('frccaptcha');
    dealer = this.store.selectSignal(DealerFeature.state.selectLoadedDealer);
    dealerName = this.store.selectSignal(DealerFeature.state.selectDealerName);
    formSent = this.store.selectSignal(ContactFeature.state.selectSubmitFormSuccess);
    loading = this.store.selectSignal(ContactFeature.state.selectSubmitFormLoading);
    hasSubdealers = this.store.selectSignal(DealerFeature.state.selectHasSubDealers);
    GENDER = GENDER;
    getErrorMessage = getErrorMessage;

    disclaimer =
        'Die von Ihnen angegebenen personenbezogenen Daten werden durch den zuständigen Vertragshändler allein zur Bearbeitung Ihrer Kontaktanfrage verarbeitet. Weitere Hinweise zur Datenverarbeitung und Ihren Rechten als betroffene Person finden Sie in unserer Datenschutzerklärung unter';

    heroImage = computed(() => ({
        image: 'https://res.cloudinary.com/hmd-pw-dfnosvs4u/image/upload/v1729501775/_defaults/Head-Img%20E-Mailtemplate%20Kontaktformular.png',
        desktop: { width: 1169, height: 835 },
        tablet: { width: 512, height: 390 },
        mobile: { width: 767, height: 548 },
    }));

    options: Array<{ label: string; value: string }> = [
        { label: 'Verkauf & Beratung', value: 'Verkauf & Beratung' },
        { label: 'Service & Werkstatt', value: 'Service & Werkstatt' },
        { label: 'Teiledienst', value: 'Teiledienst' },
        { label: 'Allgemeines', value: 'Allgemeines' },
    ];

    form = new FormGroup({
        gender: new FormControl<GENDER>(GENDER.FEMALE, { nonNullable: true }),
        firstName: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        lastName: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        email: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.email],
        }),
        phone: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, numericValidator()],
        }),
        topic: new FormControl<string | null>(null, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        captchaSolution: new FormControl<string>(isDevMode() ? 'done' : '', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        dealer: new FormControl<string | null>({ value: null, disabled: true }),
        message: new FormControl<string | null>(null),
    });

    selectedDealer = toSignal(this.form.controls.dealer.valueChanges);
    selectedDealerName = computed(() => {
        const dealerSlug = this.selectedDealer();
        if (!dealerSlug) return;
        const dealerOptions = this.dealerOptions();
        const selectedDealer = dealerOptions.find((d) => d.value === dealerSlug);
        return selectedDealer ? selectedDealer.label : undefined;
    });

    dealerOptions = computed<Array<{ label: string; value: string }>>(() => {
        const dealer = this.dealer();

        if (dealer.subDealers.length > 0) {
            this.form.controls.dealer.enable();
            this.form.get('dealer')?.setValidators([Validators.required]);
        }

        return [
            {
                value: dealer.slug,
                label: `${[dealer.name, dealer.name2, dealer.name3].filter((n) => !!n.trim()).join(' ')}, ${dealer.city}`,
            },
            ...dealer.subDealers.map((d) => ({
                value: d.slug,
                label: `${[d.name, d.name2, d.name3].filter((n) => !!n.trim()).join(' ')}, ${d.city}`,
            })),
        ];
    });

    constructor() {
        effect(() => {
            const el = this.friendlyCaptcha()?.nativeElement;
            if (this.friendlyCaptcha() && el) {
                const widget = new WidgetInstance(el, {
                    doneCallback: (solution) => {
                        this.form.get('captchaSolution')?.patchValue(solution);
                        this.form.updateValueAndValidity();
                    },
                    errorCallback: (b) => {
                        console.error('error', b);
                        this.form.get('captchaSolution')?.patchValue('');
                        this.form.updateValueAndValidity();
                    },
                });
                widget.start();
            }
        });

        this.router.events.pipe(takeUntilDestroyed()).subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.store.dispatch(ContactFeature.actions.resetContactForm());
                this.form.reset();
            }
        });
    }

    submitForm() {
        this.form.markAllAsTouched();
        const value = this.form.getRawValue();
        if (!value || this.form.invalid) return;
        const genderMapping: { [key in GENDER]: 'Herr' | 'Frau' | 'Divers' } = {
            [GENDER.MALE]: 'Herr',
            [GENDER.FEMALE]: 'Frau',
            [GENDER.DIVERS]: 'Divers',
        };

        const formGender = this.form.value.gender as GENDER;
        const gender: 'Herr' | 'Frau' | 'Divers' = genderMapping[formGender] ?? 'Herr';

        const payload: ApiTypes.ContactFormPayload = {
            gender: gender,
            firstName: value.firstName ?? '',
            lastName: value.lastName ?? '',
            email: value.email ?? '',
            phone: value.phone ?? '',
            message: value.message ?? '',
            topic: value.topic as (typeof this.options)[number]['value'],
            captchaSolution: value.captchaSolution ?? '',
        };

        this.store.dispatch(
            ContactFeature.actions.submitContactForm({ payload, dealerSlug: value.dealer || undefined }),
        );
    }
}
